import { useTranslation } from 'react-i18next'
import 'flatpickr/dist/themes/material_blue.css'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import stSeat from '../../../assets/images/icons/st_seat.svg'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import { AGE_GROUPS, SEAT_TYPES } from '../../constants'
import { GET_BOAT_ROUTE_SEAT_AVAILABILITY, CALCULATE_BOOKING_PRICE } from 'graphql/booking/queries'
import { useLazyQuery } from '@apollo/client'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { useNavigate } from 'react-router-dom'
import loadingImage from 'assets/images/boat-loading.gif'
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { VIPSeatLayout } from './VIPSeatLayout'
import { StandardSeatLayout } from './StandardSeatLayout'
import {
    getSeatPrice,
    updateDepartureSeatCounts,
    updateReturnSeatCounts,
    updateSeatCounts,
    isVIPOnlyVessel,
    prepareStandardBoatLayout,
    prepareVIPOnlyBoatLayout,
} from './seatLayoutUtils'

export function BookingSeatSelection({
    tripDetails,
    passengerDetails,
    priceDetails,
    availableSeatTypeCount,
    onSeatSelection,
    disablePriceCalculation = false,
}) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [seatLayoutOpen, setSeatLayoutOpen] = useState(false)
    const [passengerList, setPassengerList] = useState([])
    const [isRedirectBooking, setIsRedirectBooking] = useState(false)
    const [isSeatSelected, setIsSeatSelected] = useState(false)
    const [isConfirmEnabled, setIsConfirmEnabled] = useState(false)

    // Round trip state
    const [currentTripType, setCurrentTripType] = useState('departure') // 'departure' or 'return'
    const [departureSeats, setDepartureSeats] = useState({})
    const [returnSeats, setReturnSeats] = useState({})
    const [departureAvailableSeats, setDepartureAvailableSeats] = useState([])
    const [returnAvailableSeats, setReturnAvailableSeats] = useState([])
    const [departureSeatCounts, setDepartureSeatCounts] = useState({ STD: 0, VIP: 0 })
    const [returnSeatCounts, setReturnSeatCounts] = useState({ STD: 0, VIP: 0 })
    
    // Seat layout key state
    const [departureSeatLayoutKey, setDepartureSeatLayoutKey] = useState('standard_vessels')
    const [returnSeatLayoutKey, setReturnSeatLayoutKey] = useState('standard_vessels')
    const [seatLayoutKey, setSeatLayoutKey] = useState('standard_vessels')

    BookingSeatSelection.propTypes = {
        tripDetails: PropTypes.shape({
            tripId: PropTypes.string.isRequired,
            startLocation: PropTypes.string.isRequired,
            endLocation: PropTypes.string.isRequired,
            departureDate: PropTypes.string.isRequired,
            routeId: PropTypes.string.isRequired,
            isRoundTrip: PropTypes.bool,
            isInternational: PropTypes.bool,
            departureTrip: PropTypes.object,
        }).isRequired,
        passengerDetails: PropTypes.object.isRequired,
        priceDetails: PropTypes.array.isRequired,
        availableSeatTypeCount: PropTypes.array.isRequired,
        onSeatSelection: PropTypes.func.isRequired,
        disablePriceCalculation: PropTypes.bool,
    }

    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)

    const [selectedPassengerIndx, setSelectedPassengerIndx] = useState(null)
    const [seatAvailableCount, setSeatAvailableCount] = useState({ STD: 0, VIP: 0 })

    const [seatLayout, setSeatLayout] = useState({
        VIP: [],
        STD: [],
    })
    const [masterAvailableSeats, setMasterAvailableSeats] = useState([])
    const [currentAvailableSeats, setCurrentAvailableSeats] = useState([])
    const [selectedSeats, setSelectedSeats] = useState({})
    const [previewSeat, setPreviewSeat] = useState({
        colIndex: null,
        seatRow: null,
        seatType: null,
        seatNumber: null,
    })

    useEffect(() => {
        if (passengerDetails?.passengers && passengerDetails.passengers?.length > 0) {
            setPassengerList(passengerDetails.passengers)
            
            // Initialize seat data for round trip
            if (tripDetails.isRoundTrip) {
                initializeRoundTripSeats()
            } else {
            fetchAvailableSeats()
            }
            
            // For selected seats - create object mapping seat_no to passenger index
            const assignedSeatsMap = {}
            passengerDetails.passengers.forEach((passenger, index) => {
                if (passenger.seat_info?.seat_no) {
                    assignedSeatsMap[passenger.seat_info.seat_no] = index
                }
            })
            setSelectedSeats(assignedSeatsMap)
        }
    }, [passengerDetails, availableSeatTypeCount, tripDetails])

    // Update seat layout when switching between departure and return trips
    useEffect(() => {
        if (tripDetails.isRoundTrip && (departureAvailableSeats.length > 0 || returnAvailableSeats.length > 0)) {
            prepareBoatLayout()
        }
    }, [currentTripType, departureSeats, returnSeats, departureAvailableSeats, returnAvailableSeats])

    // Initialize round trip seats
    const initializeRoundTripSeats = () => {
        // Initialize departure seats
        const departureSeatsData = {}
        passengerDetails.passengers.forEach((passenger, index) => {
            departureSeatsData[index] = {
                seat_no: passenger.seat_info.seat_no || null,
                seat_type_code: passenger.seat_info.seat_type_code || null,
                seat_price: passenger.seat_info.seat_price || null,
            }
        })
        setDepartureSeats(departureSeatsData)
        
        // Initialize return seats
        const returnSeatsData = {}
        passengerDetails.passengers.forEach((passenger, index) => {
            returnSeatsData[index] = {
                seat_no: passenger.return_seat_info?.seat_no || null,
                seat_type_code: passenger.return_seat_info?.seat_type_code || null,
                seat_price: passenger.return_seat_info?.seat_price || null,
            }
        })
        setReturnSeats(returnSeatsData)
        
        // Fetch departure seats first
        fetchDepartureSeats()
    }

    // Fetch departure seats
    const fetchDepartureSeats = () => {
        setCurrentTripType('departure')
        setSubmitLoading(true)
        
        getBoatRouteTripSeatAvailable({
            variables: {
                tripId: tripDetails.departureTrip.trip.trip_id,
                startLocationId: tripDetails.departureTrip.start_location_id,
                endLocationId: tripDetails.departureTrip.end_location_id,
                tripDate: tripDetails.departureTrip.trip_date,
            },
        })
    }

    // Fetch return seats
    const fetchReturnSeats = () => {
        setCurrentTripType('return')
        setSubmitLoading(true)
        
        console.log('Fetching return seats with trip details:', {
            tripId: tripDetails.tripId,
            startLocationId: tripDetails.startLocation,
            endLocationId: tripDetails.endLocation,
            tripDate: tripDetails.departureDate
        })
        
        getBoatRouteTripSeatAvailable({
            variables: {
                tripId: tripDetails.tripId,
                startLocationId: tripDetails.startLocation, // Return trip start location
                endLocationId: tripDetails.endLocation, // Return trip end location
                tripDate: tripDetails.departureDate, // Return date
            },
        })
    }

    // Query to get Boat Details by id
    const [getBoatRouteTripSeatAvailable, { loading: availableListLoading, error: availableListError }] = useLazyQuery(
        GET_BOAT_ROUTE_SEAT_AVAILABILITY,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                setSubmitLoading(false)
                console.log('Seat availability query completed:', {
                    success: data.getBoatRouteTripSeatAvailability.success,
                    code: data.getBoatRouteTripSeatAvailability.code,
                    currentTripType: currentTripType,
                    seatsCount: data.getBoatRouteTripSeatAvailability.seats?.length || 0,
                    seatLayoutKey: data.getBoatRouteTripSeatAvailability.seat_layout_key
                })
                
                if (
                    data.getBoatRouteTripSeatAvailability.success &&
                    data.getBoatRouteTripSeatAvailability.code === '200'
                ) {
                    const layoutKey = data.getBoatRouteTripSeatAvailability.seat_layout_key || 'standard_vessels'
                    
                    if (currentTripType === 'departure') {
                        console.log('Setting departure seats:', data.getBoatRouteTripSeatAvailability.seats?.length || 0)
                        setDepartureAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setMasterAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setCurrentAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setDepartureSeatLayoutKey(layoutKey)
                        setSeatLayoutKey(layoutKey)
                        updateDepartureSeatCounts(data.getBoatRouteTripSeatAvailability.seats, setDepartureSeatCounts)
                    } else {
                        console.log('Setting return seats:', data.getBoatRouteTripSeatAvailability.seats?.length || 0)
                        setReturnAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setMasterAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setCurrentAvailableSeats(data.getBoatRouteTripSeatAvailability.seats)
                        setReturnSeatLayoutKey(layoutKey)
                        setSeatLayoutKey(layoutKey)
                        updateReturnSeatCounts(data.getBoatRouteTripSeatAvailability.seats, setReturnSeatCounts)
                    }
                    // Update seat counts based on current trip type
                    if (currentTripType === 'departure') {
                        updateDepartureSeatCounts(data.getBoatRouteTripSeatAvailability.seats, setDepartureSeatCounts)
                    } else {
                        updateReturnSeatCounts(data.getBoatRouteTripSeatAvailability.seats, setReturnSeatCounts)
                    }
                    updateSeatCounts(data.getBoatRouteTripSeatAvailability.seats, setSeatAvailableCount)
                    prepareBoatLayout()
                } else if (data.getBoatRouteTripSeatAvailability.code === 'TRIP_INVALID_START_TIME') {
                    setErrorMessage(data.getBoatRouteTripSeatAvailability.message)
                    setShowErrDialog(true)
                    setIsRedirectBooking(true)
                } else {
                    setErrorMessage(data.getBoatRouteTripSeatAvailability.message)
                    setShowErrDialog(true)
                }
            },
            onError: error => {
                setSubmitLoading(false)
                setErrorMessage(error.message)
                setShowErrDialog(true)
            },
        }
    )

    // Seat count functions moved to seatLayoutUtils

    // Helper function moved to seatLayoutUtils
    const getSeatPriceLocal = (seatType, ageGroup, tripData = null) => {
        return getSeatPrice(seatType, ageGroup, tripData, priceDetails)
    }

    const fetchAvailableSeats = () => {
        setSubmitLoading(true)
        getBoatRouteTripSeatAvailable({
            variables: {
                tripId: tripDetails.tripId,
                startLocationId: tripDetails.startLocation,
                endLocationId: tripDetails.endLocation,
                tripDate: tripDetails.departureDate,
            },
        })
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
        if (isRedirectBooking) {
            navigate('/boat-booking')
        }
    }

    // Column labels moved to seatLayoutUtils

    // Open layout Slide
    const handleChooseSeat = (status, index) => {
        setSeatLayoutOpen(true)
        setSelectedPassengerIndx(index)
        
        // Get currently selected seat for this passenger
        let currentSeat = null
        if (tripDetails.isRoundTrip) {
            if (currentTripType === 'departure') {
                currentSeat = departureSeats[index]
            } else {
                currentSeat = returnSeats[index]
            }
        } else {
            currentSeat = passengerList[index]?.seat_info
        }
        
        // If seat is already selected, populate the preview
        if (currentSeat?.seat_no) {
            // Parse seat number (e.g., "1A" -> row: 1, column: "A")
            const seatNo = currentSeat.seat_no
            const row = seatNo.slice(0, -1) // Get all characters except last (row number)
            const column = seatNo.slice(-1) // Get last character (column letter)
            const columns = ['A', 'B', 'C', 'D']
            const colIndex = columns.indexOf(column)
            
            setPreviewSeat({
                colIndex: colIndex,
                seatRow: parseInt(row),
                seatType: currentSeat.seat_type_code,
                seatNumber: seatNo,
            })
            setIsConfirmEnabled(false) // Disable confirm until user selects a different seat
        } else {
            // No seat selected yet, clear preview
            setPreviewSeat({
                colIndex: null,
                seatRow: null,
                seatType: null,
                seatNumber: null,
            })
            setIsConfirmEnabled(false)
        }
        
        prepareBoatLayout()
    }

    //click Seat to select
    const setSelectedSeat = (colIndex, seatNumber, seatType) => {
        // For VIP-only layout, seatNumber is already complete (e.g., "2A")
        // For standard layout, construct it from row number and column
        const columns = ['A', 'B', 'C', 'D']
        const seatKey = colIndex === null ? seatNumber : `${seatNumber}${columns[colIndex]}`
        
        // Determine actual seat type based on layout key
        let actualSeatType = seatType
        if (tripDetails.isRoundTrip) {
            const currentLayoutKey = currentTripType === 'departure' ? departureSeatLayoutKey : returnSeatLayoutKey
            if (currentLayoutKey === 'all_std_seat_vessels' && seatType === 'VIP') {
                actualSeatType = 'STD'
            }
        } else {
            if (seatLayoutKey === 'all_std_seat_vessels' && seatType === 'VIP') {
                actualSeatType = 'STD'
            }
        }
        
        if (tripDetails.isRoundTrip) {
            if (currentTripType === 'departure') {
                // Update departure seats
                setDepartureSeats(prev => {
                    const newSeats = {
                        ...prev,
                        [selectedPassengerIndx]: {
                            seat_no: seatKey,
                            seat_type_code: actualSeatType,
                            seat_price: null,
                        }
                    }
                    return newSeats
                })
                
                // Update passenger list with departure seat info
                const updatedPassengers = [...passengerList]
                updatedPassengers[selectedPassengerIndx].seat_info.seat_no = seatKey
                updatedPassengers[selectedPassengerIndx].seat_info.seat_type_code = actualSeatType
                setPassengerList(updatedPassengers)
            } else {
                // Update return seats
                setReturnSeats(prev => {
                    const newSeats = {
                        ...prev,
                        [selectedPassengerIndx]: {
                            seat_no: seatKey,
                            seat_type_code: actualSeatType,
                            seat_price: null,
                        }
                    }
                    return newSeats
                })
                
                // Update passenger list with return seat info
                const updatedPassengers = [...passengerList]
                if (!updatedPassengers[selectedPassengerIndx].return_seat_info) {
                    updatedPassengers[selectedPassengerIndx].return_seat_info = {}
                }
                updatedPassengers[selectedPassengerIndx].return_seat_info.seat_no = seatKey
                updatedPassengers[selectedPassengerIndx].return_seat_info.seat_type_code = actualSeatType
                setPassengerList(updatedPassengers)
            }
        }  else {
            // One-way trip - FIXED VERSION
            const oldSeatNo = passengerList[selectedPassengerIndx].seat_info.seat_no
            passengerList[selectedPassengerIndx].passenger.passenger_id =
                passengerList[selectedPassengerIndx].passenger.id || uuidv4()
            passengerList[selectedPassengerIndx].seat_info.seat_type_code = actualSeatType
            passengerList[selectedPassengerIndx].seat_info.seat_no = seatKey
            setPassengerList([...passengerList])
            
            // Update selectedSeats object for one-way trips
            const updatedSelectedSeats = { ...selectedSeats }
            // Remove old seat assignment if exists
            if (oldSeatNo && updatedSelectedSeats[oldSeatNo] === selectedPassengerIndx) {
                delete updatedSelectedSeats[oldSeatNo]
            }
            // Add new seat assignment
            updatedSelectedSeats[seatKey] = selectedPassengerIndx
            setSelectedSeats(updatedSelectedSeats)
        }
        
        setPreviewSeat({
            colIndex: null,
            seatRow: null,
            seatType: null,
            seatNumber: null,
        })
        setIsConfirmEnabled(false)
        setIsSeatSelected(!isSeatSelected)
        setSeatLayoutOpen(false)
    }

    // Prepare Boat Layout JSON
    const prepareBoatLayout = () => {
        console.log('Preparing boat layout for trip type:', currentTripType)
        console.log('Departure available seats:', departureAvailableSeats.length)
        console.log('Return available seats:', returnAvailableSeats.length)
        
        // Determine which layout key to use
        let currentLayoutKey = seatLayoutKey
        if (tripDetails.isRoundTrip) {
            currentLayoutKey = currentTripType === 'departure' ? departureSeatLayoutKey : returnSeatLayoutKey
        }
        
        console.log('Current seat layout key:', currentLayoutKey)
        
        // Get available seats based on trip type
        const availableSeatsToCheck = tripDetails.isRoundTrip
            ? (currentTripType === 'departure' ? departureAvailableSeats : returnAvailableSeats)
            : masterAvailableSeats
        
        // Get assigned seats as an object mapping seat_no to passenger index
        let assignedSeatsMap = {}
        let assignedSeatsArray = []
        if (tripDetails.isRoundTrip) {
            if (currentTripType === 'departure') {
                Object.entries(departureSeats).forEach(([index, seat]) => {
                    if (seat.seat_no) {
                        assignedSeatsMap[seat.seat_no] = parseInt(index)
                        assignedSeatsArray.push(seat.seat_no)
                    }
                })
            } else {
                Object.entries(returnSeats).forEach(([index, seat]) => {
                    if (seat.seat_no) {
                        assignedSeatsMap[seat.seat_no] = parseInt(index)
                        assignedSeatsArray.push(seat.seat_no)
                    }
                })
            }
        } else {
            passengerList.forEach((item, index) => {
                if (item.seat_info.seat_no) {
                    assignedSeatsMap[item.seat_info.seat_no] = index
                    assignedSeatsArray.push(item.seat_info.seat_no)
                }
            })
        }
        
        setSelectedSeats(assignedSeatsMap)
        
        // Use utility functions to prepare layout based on seat_layout_key
        if (isVIPOnlyVessel(currentLayoutKey)) {
            console.log('Using VIP-only custom layout for key:', currentLayoutKey)
            const layout = prepareVIPOnlyBoatLayout(availableSeatsToCheck, assignedSeatsArray)
            setSeatLayout(layout)
            console.log('Final VIP-only layout - VIP:', layout.VIP.length, 'STD:', layout.STD.length)
            return
        }
        
        // Use the correct available seats based on current trip type
        // Don't filter out assigned seats - they should be shown in the layout with selected state
        let availableSeats = []
        if (tripDetails.isRoundTrip) {
            if (currentTripType === 'departure') {
                availableSeats = departureAvailableSeats
            } else {
                availableSeats = returnAvailableSeats
            }
        } else {
            availableSeats = masterAvailableSeats
        }
        
        console.log('Available seats for current trip type:', availableSeats.length)
        
        // Prepare standard boat layout - pass assignedSeatsArray to mark them as selected
        const layout = prepareStandardBoatLayout(availableSeats, assignedSeatsArray, currentLayoutKey)
        setSeatLayout(layout)
        
        console.log('Final seat layout - VIP:', layout.VIP.length, 'STD:', layout.STD.length)
    }

    // Prepare Passenger Seat Price
    const [calculateBookingCost, { loading: calcualtePriceLoading, error: calcualtePriceError }] = useLazyQuery(
        CALCULATE_BOOKING_PRICE,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.calculateBookingCost.success && data.calculateBookingCost.code === '200') {
                    
                    const priceData = data.calculateBookingCost.price
                    const passengerPrices = priceData.passengers
                    
                    // Update passenger details with new price information
                    const updatedPassengerDetails = passengerDetails.passengers.map((passenger, index) => {
                        // For round trips, we need to handle both departure and return prices
                        if (tripDetails.isRoundTrip) {
                            // For round trips, match by travel_date since passenger_id might be the same
                            // Normalize dates to YYYY-MM-DD format for comparison
                            const normalizedDepartureDate = tripDetails.departureTrip?.trip_date 
                                ? moment(tripDetails.departureTrip.trip_date).format('YYYY-MM-DD')
                                : null
                            const normalizedReturnDate = tripDetails.returnTrip?.trip_date 
                                ? moment(tripDetails.returnTrip.trip_date).format('YYYY-MM-DD')
                                : null
                            
                            const departurePrice = normalizedDepartureDate 
                                ? passengerPrices.find(p => p.travel_date === normalizedDepartureDate)
                                : null
                            const returnPrice = normalizedReturnDate 
                                ? passengerPrices.find(p => p.travel_date === normalizedReturnDate)
                                : null
                                
                            // Update departure seat info with price and preserve seat data
                            if (departurePrice) {
                                passenger.seat_info.seat_price = departurePrice.price.price_before_discount
                                // Preserve departure seat info from the state
                                passenger.seat_info.departure_seat_no = departureSeats[index]?.seat_no
                                passenger.seat_info.departure_seat_type_code = departureSeats[index]?.seat_type_code
                            }
                            
                            // Update return seat info with price and preserve seat data
                            if (returnPrice) {
                                if (!passenger.return_seat_info) {
                                    passenger.return_seat_info = {}
                                }
                                passenger.return_seat_info.seat_price = returnPrice.price.price_before_discount
                                // Preserve return seat info from the state
                                passenger.return_seat_info.seat_no = returnSeats[index]?.seat_no
                                passenger.return_seat_info.seat_type_code = returnSeats[index]?.seat_type_code
                            }
                        } else {
                            // One-way trip - find the matching passenger price by index
                            const matchedPassenger = passengerPrices[index]
                            if (matchedPassenger) {
                                passenger.seat_info.seat_price = matchedPassenger.price.price_before_discount
                            }
                        }
                        
                        return passenger
                    })
                    
                    // Use the total price with VAT as the total cost
                    const totalCost = priceData.total_price_with_vat
                    
                    
                    onSeatSelection({
                        passengers: updatedPassengerDetails,
                        totalCost: totalCost,
                        priceBreakdown: {
                            totalPriceBeforeDiscount: priceData.total_price_before_discount,
                            totalDiscount: priceData.total_discount,
                            totalPriceAfterDiscount: priceData.total_price_after_discount,
                            totalVat: priceData.total_vat,
                            totalPriceWithVat: priceData.total_price_with_vat,
                            currency: priceData.user_preferred_currency,
                            passengers: priceData.passengers
                        }
                    })
                } else {
                    setErrorMessage(data.calculateBookingCost.message)
                    setShowErrDialog(true)
                }
            },
            onError: error => {
                setErrorMessage(error.message)
                setShowErrDialog(true)
            },
        }
    )

    const preparePassengerPrice = () => {
        // Validate seat selection based on trip type
        if (tripDetails.isRoundTrip) {
            // Check if all passengers have seats for both trips
            const departureSeatsComplete = Object.values(departureSeats).every(seat => seat.seat_no)
            const returnSeatsComplete = Object.values(returnSeats).every(seat => seat.seat_no)
            
            if (!departureSeatsComplete || !returnSeatsComplete) {
                setErrorMessage('Please select seats for all passengers for both departure and return trips')
                setShowErrDialog(true)
                return
            }
        } else {
            if (passengerList?.length !== Object.keys(selectedSeats || {}).length) {
                setErrorMessage('Please select seat for all passengers')
                setShowErrDialog(true)
                return
            }
        }
        
        // In reschedule mode, bypass price calculation and return selected seats immediately
        if (disablePriceCalculation) {
            onSeatSelection({
                passengers: passengerList,
                totalCost: 0,
                priceBreakdown: null,
            })
            return
        }
        
        const formattedData = {
            booking: {
                user_preferred_currency: "BHD", // TODO: Make this configurable from user preferences or environment
                trip_segments: [],
            },
        }
        
        if (tripDetails.isRoundTrip) {
            // Add departure trip segment
            formattedData.booking.trip_segments.push({
                segment_order: 1,
                travel_date: tripDetails.departureTrip?.trip_date 
                    ? moment(tripDetails.departureTrip.trip_date).format('YYYY-MM-DD')
                    : moment(tripDetails.departureDate).format('YYYY-MM-DD'),
                route_id: tripDetails.departureTrip.route.route_id,
                trip_id: tripDetails.departureTrip.trip.trip_id,
                trip_location: {
                    start_location_id: tripDetails.departureTrip.start_location_id,
                    end_location_id: tripDetails.departureTrip.end_location_id,
                },
                passengers: passengerDetails.passengers.map((info, index) => {
                    const dob = info.passenger.date_of_birth 
                        ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').isValid()
                            ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                            : moment(info.passenger.date_of_birth).format('YYYY-MM-DD')
                        : '1990-01-01'
                    return {
                        passenger: {
                            passenger_id: info.passenger.passenger_id || index.toString(),
                            given_name: info.passenger.given_name,
                            family_name: info.passenger.family_name,
                            date_of_birth: dob,
                            nationality_code: info.passenger.nationality_code || null,
                            special_needs: info.passenger.special_needs,
                        },
                        seat_info: {
                            seat_no: departureSeats[index]?.seat_no,
                            seat_type_code: departureSeats[index]?.seat_type_code,
                        },
                    }
                }),
            })
            
            // Add return trip segment
            formattedData.booking.trip_segments.push({
                segment_order: 2,
                travel_date: tripDetails.returnTrip?.trip_date 
                    ? moment(tripDetails.returnTrip.trip_date).format('YYYY-MM-DD')
                    : moment(tripDetails.departureDate).format('YYYY-MM-DD'),
                        route_id: tripDetails.routeId,
                        trip_id: tripDetails.tripId,
                        trip_location: {
                            start_location_id: tripDetails.startLocation,
                            end_location_id: tripDetails.endLocation,
                        },
                passengers: passengerDetails.passengers.map((info, index) => {
                            const dob = info.passenger.date_of_birth 
                        ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').isValid()
                            ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                            : moment(info.passenger.date_of_birth).format('YYYY-MM-DD')
                        : '1990-01-01'
                            return {
                                passenger: {
                            passenger_id: info.passenger.passenger_id || index.toString(),
                                    given_name: info.passenger.given_name,
                                    family_name: info.passenger.family_name,
                                    date_of_birth: dob,
                                    nationality_code: info.passenger.nationality_code || null,
                                    special_needs: info.passenger.special_needs,
                                },
                                seat_info: {
                            seat_no: returnSeats[index]?.seat_no,
                            seat_type_code: returnSeats[index]?.seat_type_code,
                                },
                            }
                        }),
            })
        } else {
            // One-way trip
            formattedData.booking.trip_segments.push({
                segment_order: 1,
                travel_date: moment(tripDetails.departureDate).format('YYYY-MM-DD'),
                route_id: tripDetails.routeId,
                trip_id: tripDetails.tripId,
                trip_location: {
                    start_location_id: tripDetails.startLocation,
                    end_location_id: tripDetails.endLocation,
                },
                passengers: passengerDetails.passengers.map((info, index) => {
                    const dob = info.passenger.date_of_birth 
                        ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').isValid()
                            ? moment(info.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
                            : moment(info.passenger.date_of_birth).format('YYYY-MM-DD')
                        : '1990-01-01'
                    return {
                        passenger: {
                            passenger_id: info.passenger.passenger_id || index.toString(),
                            given_name: info.passenger.given_name,
                            family_name: info.passenger.family_name,
                            date_of_birth: dob,
                            nationality_code: info.passenger.nationality_code || null,
                            special_needs: info.passenger.special_needs,
                        },
                        seat_info: {
                            seat_no: info.seat_info.seat_no,
                            seat_type_code: info.seat_info.seat_type_code,
                        },
                    }
                }),
            })
        }
        
        
        calculateBookingCost({
            variables: formattedData,
        })
    }

    // Handle next step for round trip
    const handleNextStep = () => {
        if (tripDetails.isRoundTrip) {
            if (currentTripType === 'departure') {
                // Check if all departure seats are selected
                const departureSeatsComplete = Object.values(departureSeats).every(seat => seat.seat_no)
                if (departureSeatsComplete) {
                    // Move to return trip seat selection
                    setCurrentTripType('return')
                    fetchReturnSeats()
                } else {
                    setErrorMessage('Please select departure seats for all passengers first')
                    setShowErrDialog(true)
                }
            } else {
                // Check if all return seats are selected
                const returnSeatsComplete = Object.values(returnSeats).every(seat => seat.seat_no)
                if (returnSeatsComplete) {
                    // Both trips complete, calculate price
                    preparePassengerPrice()
                } else {
                    setErrorMessage('Please select return seats for all passengers')
                    setShowErrDialog(true)
                }
            }
        } else {
            // One-way trip
            preparePassengerPrice()
        }
    }

    // Helper function moved to VIPSeatLayout component

    return (
        <>
            {tripDetails.isRoundTrip && (
                <div className="mb-6">
                    <p className="text-base leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-4">
                        {t('Round Trip Seat Selection')} - Current: {currentTripType === 'departure' ? t('Departure') : t('Return')}
                    </p>
                    <div className="flex gap-4 mb-4">
                        <button
                            onClick={fetchDepartureSeats}
                            className={`px-4 py-2 rounded-md ${
                                currentTripType === 'departure'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                            {t('Departure Trip')}
                        </button>
                        <button
                            onClick={fetchReturnSeats}
                            className={`px-4 py-2 rounded-md ${
                                currentTripType === 'return'
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-200 text-gray-700'
                            }`}
                        >
                            {t('Return Trip')}
                        </button>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50  rounded-lg mb-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center ">
                                <div className="w-2 h-2 bg-blue-500 "></div>
                                <span className="text-sm font-semibold text-blue-900">
                                    Current Status:
                                </span>
                            </div>
                            <span className="text-sm font-medium text-blue-700 bg-blue-100   rounded-full">
                                {currentTripType === 'departure' ? 'Selecting departure seats' : 'Selecting return seats'}
                            </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center justify-between bg-white p-3 rounded-md border border-blue-100">
                                <span className="text-sm font-medium text-gray-700">Departure seats selected:</span>
                                <span className="text-sm font-bold text-blue-600">
                                    {Object.values(departureSeats).filter(seat => seat.seat_no).length}/{passengerList.length}
                                </span>
                            </div>
                            
                            <div className="flex items-center justify-between bg-white p-3 rounded-md border border-blue-100">
                                <span className="text-sm font-medium text-gray-700">Return seats selected:</span>
                                <span className="text-sm font-bold text-blue-600">
                                    {Object.values(returnSeats).filter(seat => seat.seat_no).length}/{passengerList.length}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-4 mb-5">
                <div className="w-auto flex-1">
                    <div className="mb-6">
                        <p className="text-base leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-4">
                            {t('Available Seats')} - {currentTripType === 'departure' ? t('Departure') : t('Return')}
                        </p>
                        <div className="flex flex-row gap-6">
                            {tripDetails.isRoundTrip ? (
                                currentTripType === 'departure' ? (
                                    departureSeatLayoutKey === 'all_std_seat_vessels' ? (
                                        // For all_std_seat_vessels, show only STD seats with combined count
                                        <p key="STD" className="flex text-sm font-bold text-gray-600">
                                            <img
                                                src={stSeat}
                                                className="w-[20px] me-2"
                                                alt="Standard Seat"
                                            />
                                            {SEAT_TYPES['STD']} : {(departureSeatCounts.STD || 0) + (departureSeatCounts.VIP || 0)}
                                        </p>
                                    ) : (
                                        // For standard_vessels, show VIP and STD separately
                                        Object.keys(departureSeatCounts).map(function (seatType, index) {
                                            return (
                                                <p key={seatType} className="flex text-sm font-bold text-gray-600">
                                                    <img
                                                        src={seatType == 'VIP' ? vipSeat : stSeat}
                                                        className="w-[20px] me-2"
                                                        alt={seatType == 'VIP' ? 'VIP Seat' : 'Standard Seat'}
                                                    />
                                                    {SEAT_TYPES[seatType]} : {departureSeatCounts[seatType] || 0}
                                                </p>
                                            )
                                        })
                                    )
                                ) : (
                                    returnSeatLayoutKey === 'all_std_seat_vessels' ? (
                                        // For all_std_seat_vessels, show only STD seats with combined count
                                        <p key="STD" className="flex text-sm font-bold text-gray-600">
                                            <img
                                                src={stSeat}
                                                className="w-[20px] me-2"
                                                alt="Standard Seat"
                                            />
                                            {SEAT_TYPES['STD']} : {(returnSeatCounts.STD || 0) + (returnSeatCounts.VIP || 0)}
                                        </p>
                                    ) : (
                                        // For standard_vessels, show VIP and STD separately
                                        Object.keys(returnSeatCounts).map(function (seatType, index) {
                                            return (
                                                <p key={seatType} className="flex text-sm font-bold text-gray-600">
                                                    <img
                                                        src={seatType == 'VIP' ? vipSeat : stSeat}
                                                        className="w-[20px] me-2"
                                                        alt={seatType == 'VIP' ? 'VIP Seat' : 'Standard Seat'}
                                                    />
                                                    {SEAT_TYPES[seatType]} : {returnSeatCounts[seatType] || 0}
                                                </p>
                                            )
                                        })
                                    )
                                )
                            ) : (
                                seatLayoutKey === 'all_std_seat_vessels' ? (
                                    // For all_std_seat_vessels, show only STD seats with combined count
                                    <p key="STD" className="flex text-sm font-bold text-gray-600">
                                        <img
                                            src={stSeat}
                                            className="w-[20px] me-2"
                                            alt="Standard Seat"
                                        />
                                        {SEAT_TYPES['STD']} : {(seatAvailableCount.STD || 0) + (seatAvailableCount.VIP || 0)}
                                    </p>
                                ) : (
                                    // For standard_vessels, show VIP and STD separately
                                    seatAvailableCount &&
                                    Object.keys(seatAvailableCount).map(function (seatType, index) {
                                        return (
                                            <p key={seatType} className="flex text-sm font-bold text-gray-600">
                                                <img
                                                    src={seatType == 'VIP' ? vipSeat : stSeat}
                                                    className="w-[20px] me-2"
                                                    alt={seatType == 'VIP' ? 'VIP Seat' : 'Standard Seat'}
                                                />
                                                {SEAT_TYPES[seatType]} : {seatAvailableCount[seatType] || 0}
                                            </p>
                                        )
                                    })
                                )
                            )}
                        </div>
                    </div>
                    <div>
                        <p className="text-base leading-7 text-gray-900 border-b-[0px] border-solid border-gray bg-[#f9f9f9] p-3 pb-2 mb-1">
                            {t('Passenger Info')}
                        </p>
                        <div className="flex flex-row gap-6">
                            <ul className="divide-y divide-gray-100 w-full">
                                {passengerList?.map(function (info, index) {
                                    let currentSeat = null
                                    if (tripDetails.isRoundTrip) {
                                        if (currentTripType === 'departure') {
                                            currentSeat = departureSeats[index]
                                        } else {
                                            currentSeat = returnSeats[index]
                                        }
                                    } else {
                                        currentSeat = info.seat_info
                                    }
                                    
                                    return (
                                        <li key={index} className="py-2 border-b-[1px] border-solid border-gray">
                                            <div className="flex items-center gap-x-3">
                                                <h3 className="flex-auto text-[15px] font-bold leading-6 text-gray-900">
                                                    <span className="font-[400] ">
                                                        {AGE_GROUPS[info.seat_info.age_group_code]}{' '}
                                                    </span>
                                                    <br />
                                                    {info.passenger.given_name} {info.passenger.family_name}
                                                </h3>
                                                <button
                                                    type="button"
                                                    onClick={() => handleChooseSeat('NEW', index)}
                                                    className={`${
                                                        !currentSeat?.seat_no
                                                            ? 'rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm'
                                                            : 'rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {!currentSeat?.seat_no
                                                        ? t('Select Seat')
                                                        : t('Change Seat')}
                                                </button>
                                            </div>
                                            {currentSeat?.seat_no && (
                                                <div className="flex flex-row gap-6 justify-between mt-3">
                                                    <div className="flex gap-6">
                                                        <p className="flex text-sm font-semibold text-gray-600">
                                                            <img
                                                                src={
                                                                    currentSeat.seat_type_code == 'VIP'
                                                                        ? vipSeat
                                                                        : stSeat
                                                                }
                                                                className="w-[20px] me-2"
                                                            />{' '}
                                                            {currentSeat.seat_type_code == 'VIP'
                                                                ? 'VIP'
                                                                : 'Standard'}{' '}
                                                        </p>
                                                        <p className="flex items-center text-sm font-semibold text-gray-600">
                                                            <span className="block w-5 h-5 me-2 rounded-md bg-[#E0E8E9]"></span>{' '}
                                                            {currentSeat.seat_no}
                                                        </p>
                                                    </div>
                                                    {/* Price Display - Hidden for international trips and reschedule mode */}
                                                    {!tripDetails.isInternational && !disablePriceCalculation && (
                                                        <div className="flex items-center">
                                                            <span className="text-sm font-bold text-primary">
                                                                {getSeatPriceLocal(
                                                                    currentSeat.seat_type_code, 
                                                                    info.seat_info.age_group_code,
                                                                    currentTripType === 'departure' ? tripDetails.departureTrip : tripDetails.returnTrip
                                                                )} BHD
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={seatLayoutOpen} onClose={setSeatLayoutOpen} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
                />
                <div className="fixed inset-0" />

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                    <div className="px-4 sm:px-6">
                                        <div className="flex items-start justify-between">
                                            <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                                Select Seat for{' '}
                                                {passengerList[selectedPassengerIndx]?.passenger?.given_name}{' '}
                                                ({currentTripType === 'departure' ? t('Departure') : t('Return')})
                                            </DialogTitle>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setSeatLayoutOpen(false)}
                                                    className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                                >
                                                    <span className="absolute -inset-2.5" />
                                                    <span className="sr-only">Close panel</span>
                                                    <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                        <div className="flex flex-col md:flex-row justify-between border-b-[1px] border-solid border-gray pb-4 mb-4">
                                            <p className="font-md font-semibold mb-3 md:mb-0">Seat Info</p>
                                            <div className="flex gap-4">
                                                <p className="flex items-center text-sm">
                                                    <span className="block w-5 h-5 me-2 rounded-md bg-white border-solid border-2 border-gray"></span>{' '}
                                                    {t('Available')}
                                                </p>
                                                <p className="flex items-center text-sm">
                                                    <span className="block w-5 h-5 me-2 rounded-md bg-[#E0E8E9]"></span>{' '}
                                                    {t('Booked')}
                                                </p>
                                                <p className="flex items-center text-sm">
                                                    <span className="block w-5 h-5 me-2 rounded-md bg-[#128472]"></span>{' '}
                                                    {t('Selected')}
                                                </p>
                                            </div>
                                        </div>
                                        {/* Boat Layout */}
                                        {availableListLoading && (
                                            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                                                <img alt="logo" src={loadingImage} className="w-[250px]" />
                                                <p className="pt-3 text-primary font-semibold">
                                                    Loading, please wait...
                                                </p>
                                            </div>
                                        )}{' '}
                                        {!availableListLoading &&
                                            !availableListError &&
                                            currentAvailableSeats?.length > 0 && (
                                                <>
                                                    {/* Check if VIP-only layout based on seat_layout_key */}
                                                    {isVIPOnlyVessel(tripDetails.isRoundTrip 
                                                        ? (currentTripType === 'departure' ? departureSeatLayoutKey : returnSeatLayoutKey)
                                                        : seatLayoutKey) ? (
                                                        /* VIP-Only Layout Component */
                                                        <VIPSeatLayout
                                                            seatLayout={seatLayout}
                                                            selectedSeats={selectedSeats}
                                                            selectedPassengerIndx={selectedPassengerIndx}
                                                            departureSeats={departureSeats}
                                                            returnSeats={returnSeats}
                                                            passengerList={passengerList}
                                                            tripDetails={tripDetails}
                                                            currentTripType={currentTripType}
                                                            setIsConfirmEnabled={setIsConfirmEnabled}
                                                            setPreviewSeat={setPreviewSeat}
                                                        />
                                                    ) : (
                                                        /* Standard Layout Component */
                                                        <StandardSeatLayout
                                                            seatLayout={seatLayout}
                                                            selectedSeats={selectedSeats}
                                                            selectedPassengerIndx={selectedPassengerIndx}
                                                            tripDetails={tripDetails}
                                                            currentTripType={currentTripType}
                                                            departureSeatLayoutKey={departureSeatLayoutKey}
                                                            returnSeatLayoutKey={returnSeatLayoutKey}
                                                            seatLayoutKey={seatLayoutKey}
                                                            setIsConfirmEnabled={setIsConfirmEnabled}
                                                            setPreviewSeat={setPreviewSeat}
                                                        />
                                                    )}
                                                    <div className="mt-[10px] ">
                                                        <div className="flex justify-between w-full">
                                                            <p className="mb-[20px]">
                                                                {t('Selected Seat Type')}-
                                                                <span className="font-bold text-[16px]">
                                                                    {SEAT_TYPES[previewSeat.seatType]}
                                                                </span>
                                                            </p>
                                                            <p className="mb-[20px]">
                                                                {t(' Selected Seat')}-
                                                                <span className="font-bold text-[16px]">
                                                                    {previewSeat.seatNumber}
                                                                </span>
                                                            </p>
                                                        </div>
                                                        
                                                        {/* Price Information - Hidden for international trips and reschedule mode */}
                                                        {!tripDetails.isInternational && !disablePriceCalculation && previewSeat.seatType && passengerList[selectedPassengerIndx] && (
                                                            <div className="bg-gray-50 p-4 rounded-lg mb-4">
                                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('Price Information')}</h4>
                                                                <div className="space-y-2">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm text-gray-600">
                                                                            {t('Passenger')}: {passengerList[selectedPassengerIndx]?.passenger?.given_name} {passengerList[selectedPassengerIndx]?.passenger?.family_name}
                                                                        </span>
                                                                        <span className="text-sm font-medium text-gray-900">
                                                                            {AGE_GROUPS[passengerList[selectedPassengerIndx]?.seat_info?.age_group_code]}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-sm text-gray-600">
                                                                            {t('Seat Type')}: {SEAT_TYPES[previewSeat.seatType]}
                                                                        </span>
                                                                        <span className="text-lg font-bold text-primary">
                                                                            {getSeatPriceLocal(
                                                                                previewSeat.seatType, 
                                                                                passengerList[selectedPassengerIndx]?.seat_info?.age_group_code,
                                                                                currentTripType === 'departure' ? tripDetails.departureTrip : tripDetails.returnTrip
                                                                            )} BHD
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {isConfirmEnabled && (
                                                            <button
                                                                onClick={() =>
                                                                    setSelectedSeat(
                                                                        previewSeat.colIndex,
                                                                        previewSeat.seatRow || previewSeat.seatNumber,
                                                                        previewSeat.seatType
                                                                    )
                                                                }
                                                                href="boats/boat-form"
                                                                className="block text-center w-full hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                            >
                                                                {t('Confirm')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>


            <div className="mt-[10px] flex justify-end bg-primary/[.06] pt-4 pr-0 pb-4 pl-4 rounded-md">
            <button
                    onClick={handleNextStep}
                    type="submit"
                    className="px-6 py-2 bg-teal-600 text-white rounded"
                >
                    {tripDetails.isRoundTrip 
                        ? (currentTripType === 'departure' ? t('Next: Select Return Seats') : t('Continue'))
                        : t('Continue')
                    }
                </button>
            </div>
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}
            {/* Overlay and Loading Spinner */}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
